import AddIcon from '@mui/icons-material/Add'
import {
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  SxProps,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { uniqueId } from 'lodash'
import { useNavigate, useParams } from 'react-router-dom'

import { CREATE_SCENARIO_ROUTE_ID } from '~/features/scenarios/constants'
import useGetScenarios from '~/features/scenarios/hooks/useGetScenarios'
import { EScenarioKeys } from '~/features/scenarios/models'
import { pathForScenario } from '~/features/scenarios/utils'
import rem from '~/utils/ui'

type TProps = {
  close: () => void
  sx?: SxProps
}

function ScenariosList({ close, sx = {} }: TProps) {
  const { scenarioId: routeScenarioId } = useParams()
  const theme = useTheme()
  const navigate = useNavigate()
  const { data: { data: scenarios } = {}, isLoading } = useGetScenarios()

  const handleNavigateToScenario = (scenarioId: string) => {
    navigate(pathForScenario(scenarioId))
    close()
  }

  const handleNavigateToCreateScenario = () => {
    navigate(pathForScenario(CREATE_SCENARIO_ROUTE_ID))
    close()
  }

  return (
    <Stack minHeight="40%" sx={{ ...sx }}>
      <Stack direction="row" alignItems="center">
        <Typography
          variant="h5"
          fontWeight={theme.typography.fontWeightBold}
          color={theme.palette.mainFont}
        >
          Manage Scenarios
        </Typography>
        <IconButton
          aria-label="close"
          size="large"
          onClick={handleNavigateToCreateScenario}
        >
          <AddIcon />
        </IconButton>
      </Stack>
      <List sx={{ maxHeight: '100%', overflow: 'auto' }}>
        {isLoading ? (
          <Stack
            sx={{ height: rem(100) }}
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress color="inherit" />
          </Stack>
        ) : (
          scenarios
            ?.sort((a, b) =>
              a[EScenarioKeys.name].toLocaleLowerCase() >
              b[EScenarioKeys.name].toLocaleLowerCase()
                ? 1
                : -1,
            )
            .map((scenario) => (
              <ListItemButton
                onClick={() =>
                  handleNavigateToScenario(scenario[EScenarioKeys.id])
                }
                key={uniqueId()}
                sx={{
                  backgroundColor:
                    scenario[EScenarioKeys.id] === routeScenarioId
                      ? theme.palette.tertiary2
                      : 'transparent',
                }}
              >
                <Tooltip title={scenario[EScenarioKeys.name]} placement="top">
                  <ListItemText
                    primary={scenario[EScenarioKeys.name]}
                    sx={{
                      '& .MuiTypography-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                </Tooltip>
              </ListItemButton>
            ))
        )}
      </List>
    </Stack>
  )
}

export default ScenariosList
